import { useEffect, useState } from "react";
import { FaEdit, FaEye, FaRegCommentDots } from "react-icons/fa";
import { MdDeleteForever } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { toast, ToastContainer } from "react-toastify";
import LoaderSpiner from "../LoaderSpiner";
import styles from "./ShowTicket.module.css";

const ShowTicket = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState("");
  const [editPop, setEditPop] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [removedTickets, setRemovedTickets] = useState([]);
  const [showRemovedTickets, setShowRemovedTickets] = useState(false);
  const [loader, setLoader] = useState(false);
  const [viewMessagesPopup, setViewMessagesPopup] = useState(false);
  const [sendMessagePopup, setSendMessagePopup] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [messageLoading, setMessageLoading] = useState(false);
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");
  const userId = localStorage.getItem("userId");
  const authData = JSON.parse(localStorage.getItem("authData"));
  const navigate = useNavigate();
  const handleBack = () => {
    navigate("/support");
  };

  const handleDelete = async (id) => {
    const role = localStorage.getItem("role");
    try {
      const response = await fetch(
        `https://prod.toxboxai.com/api/tickets/delete/${id}?role=${role}`,
        {
          method: "delete",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      console.log(data);

      if (data.success) {
        setTickets((prev) => prev.filter((item) => id !== item._id));
        toast.success(data.message);
      } else {
        toast.error(data.error);
      }
    } catch (error) {
      console.log("Error delete ticket", error);
      toast.error("Error delete ticket");
    }
  };

  const openEditForm = (ticket) => {
    setSelectedTicket(ticket);
    setNewStatus(ticket.status); // Pre-fill with current status
    setEditPop(true);
  };

  const updateTicketStatus = async (e) => {
    e.preventDefault();
    if (!selectedTicket) return;
    try {
      const response = await fetch(
        `https://prod.toxboxai.com/api/tickets/editStatus/${selectedTicket._id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      const data = await response.json();
      console.log(data);

      setTickets((prev) => {
        let updated = prev.map((t) =>
          t._id === selectedTicket._id ? { ...t, status: newStatus } : t
        );
        if (["pending", "in-progress"].includes(newStatus)) {
          setRemovedTickets((removed) =>
            removed.filter((t) => t._id !== selectedTicket._id)
          );
          if (!updated.some((t) => t._id === selectedTicket._id)) {
            updated.push({ ...selectedTicket, status: newStatus });
          }
        }
        return updated;
      });
      setEditPop(false);
      setSelectedTicket(null);
      toast.success(data.message);
    } catch (error) {
      console.log("Error in editing tickets", error);
      toast.error("Error in editing tickets");
    }
  };

  const getRemovedTickets = async () => {
    try {
      setLoader(true);
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");
      const role = localStorage.getItem("role");
      if (!token || !userId) {
        console.error("Token or User ID not found in localStorage");
        return;
      }
      const normalizedRole = role?.toLowerCase().trim();
      let apiEndpoint = "";
      if (normalizedRole === "owner" || normalizedRole === "subowner") {
        apiEndpoint =
          "https://prod.toxboxai.com/api/tickets/get-all-tickets/removed";
      } else {
        apiEndpoint = `https://prod.toxboxai.com/api/tickets/get-ticket/removed/${userId}`;
      }
      const res = await fetch(apiEndpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) throw new Error("Unauthorized or fetch error");

      const data = await res.json();
      setRemovedTickets(data.tickets || []);
      setLoader(false);
    } catch (err) {
      setLoader(false);
      console.error("Error fetching removed tickets:", err);
    }
  };
  const handleToggleRemovedTickets = () => {
    if (!showRemovedTickets) {
      getRemovedTickets(); // fetch only when opening
    }
    setShowRemovedTickets((prev) => !prev);
  };

  const openViewMessages = async (ticket) => {
    setSelectedTicket(ticket);
    setViewMessagesPopup(true);
    setMessageLoading(true);
    try {
      const res = await fetch(
        `https://prod.toxboxai.com/api/tickets/messages/${ticket._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      console.log(data);

      setMessages(data.messages || []);
    } catch (err) {
      toast.error("Error loading messages");
    }

    setMessageLoading(false);
  };

  // Send Message
  const openSendMessage = (ticket) => {
    setSelectedTicket(ticket);
    setNewMessage("");
    setSendMessagePopup(true);
  };

  // Send Message API Call
  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      const res = await fetch(
        `https://prod.toxboxai.com/api/tickets/messages/${selectedTicket._id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            content: newMessage,
            senderRole: role,
            senderId: userId,
            name: authData.user.name,
          }),
        }
      );

      const data = await res.json();
      if (data.success) {
        // Optionally update messages if view popup is open
        if (viewMessagesPopup) {
          setMessages((prev) => [...prev, data.message]);
        }
        setNewMessage("");
        setSendMessagePopup(false);
        toast.success("Message sent to the respective user");
      }
    } catch (err) {
      toast.error("Error sending message");
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userId");
    const role = localStorage.getItem("role");

    if (!token || !userId) {
      console.error("Token or User ID not found in localStorage");
      setLoading(false);
      return;
    }

    const getApiEndpoint = (userRole, userId) => {
      const normalizedRole = userRole?.toLowerCase().trim();
      if (normalizedRole === "owner" || normalizedRole === "subowner") {
        return "https://prod.toxboxai.com/api/tickets/get-all-tickets";
      }
      return `https://prod.toxboxai.com/api/tickets/get-tickets-by-user/${userId}`;
    };

    const apiEndpoint = getApiEndpoint(role, userId);
    setUserRole(role);

    fetch(apiEndpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Unauthorized or fetch error");
        return res.json();
      })
      .then((data) => {
        setTickets(data.tickets || []);
        console.log(tickets);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching tickets:", err);
        setLoading(false);
      });
  }, [newStatus]);

  const getPageTitle = (role) => {
    const normalizedRole = role?.toLowerCase().trim();
    if (normalizedRole === "owner" || normalizedRole === "subowner") {
      return "All System Tickets";
    }
    return "Your Tickets";
  };

  if (loading) return <LoaderSpiner />;
  if (tickets.length === 0) return <p>No tickets found.</p>;

  return (
    <div className={styles.container}>
      <ToastContainer />
      {(userRole === "participant" || userRole === "vendor") && (
        <div>
          <button className={styles.backBtn} onClick={handleBack}>
            Back
          </button>
        </div>
      )}
      <h1 className={styles.title}>{getPageTitle(userRole)}</h1>
      <div className={styles.ticketList}>
        {tickets.map((ticket) => (
          <div key={ticket._id} className={styles.ticketCard}>
            {/* Edit icon at top right */}

            <div className={styles.iconWrapper}>
              <div className={styles.editBtnBox}>
                {(userRole === "owner" || userRole === "subowner") && (
                  <button
                    className={`${styles.editBtn}`}
                    onClick={() => openEditForm(ticket)}
                  >
                    <FaEdit />
                    <span className={styles.tooltip}>Edit Status</span>
                  </button>
                )}
                <button
                  className={styles.sendMsgBtn}
                  onClick={() => openSendMessage(ticket)}
                >
                  <FaRegCommentDots />
                  <span className={styles.tooltip}>Send Message</span>
                </button>

                {/* <button
                    className={styles.viewBtn}
                    onClick={() => openViewMessages(ticket)}
                  >
                    <FaEye />
                    <span className={styles.tooltip}>View Messages</span>
                  </button> */}
                <button
                  className={styles.viewBtn}
                  onClick={() => openViewMessages(ticket)}
                >
                  <div className={styles.iconWrapper}>
                    <FaEye />
                    {ticket.messages.length >= 0 && (
                      <span className={styles.messageCountBadge}>
                        {ticket.messages.length}
                      </span>
                    )}
                  </div>
                  <span className={styles.tooltip}>View Messages</span>
                </button>
              </div>
            </div>

            {/* Ticket details */}
            <h2>{ticket.subject}</h2>
            <p>
              <strong>Description:</strong> {ticket.description}
            </p>
            <p>
              <strong>Priority:</strong> {ticket.priority}
            </p>
            <p>
              <strong>Email:</strong> {ticket.email}
            </p>
            <p>
              <strong>Ticket ID:</strong> {ticket._id}
            </p>
            <p>
              <strong>Issue Type:</strong> {ticket.Issues}
            </p>

            {/* Status + Delete icon in one row */}
            <div className={styles.actionRow}>
              <span
                className={`${styles.statusBadge} ${ticket.status.toLowerCase() === "pending"
                  ? styles.pending
                  : ticket.status.toLowerCase() === "in-progress"
                    ? styles.inProgress
                    : styles.resolved
                  }`}
              >
                Status : {ticket.status}
              </span>
              <button
                className={`${styles.deleteBtn}`}
                onClick={() => handleDelete(ticket._id)}
              >
                <MdDeleteForever />
                <span className={styles.tooltip}>Delete Ticket</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Popup Modal */}
      {editPop && (
        <div className={styles.popupOverlay}>
          <div className={styles.popup}>
            <h2>Edit Ticket Status</h2>
            <form onSubmit={updateTicketStatus}>
              <label>
                Status:
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="in-progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                </select>
              </label>
              <div className={styles.popupActions}>
                <button type="submit">Update</button>
                <button type="button" onClick={() => setEditPop(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <section className={styles.removedTicketsSection}>
        <h2 className={styles.getRemovedHeading}>Get All Removed Tickets</h2>
        <button
          onClick={handleToggleRemovedTickets}
          className={styles.showRemovedBtn}
        >
          {showRemovedTickets ? "Hide Removed Tickets" : "Show Removed Tickets"}
        </button>
        {showRemovedTickets && (
          <div className={styles.ticketList}>
            {loader ? (
              <div className={styles.loaderWrapper}>
                <ClipLoader color="#36d7b7" size={50} />
              </div>
            ) : removedTickets.length === 0 ? (
              <p className={styles.title}>No Removed Tickets Found</p>
            ) : (
              removedTickets.map((ticket) => (
                <div key={ticket._id} className={styles.ticketCard}>
                  {(userRole === "owner" || userRole === "subowner") && (
                    <div className={styles.iconWrapper}>
                      <div className={styles.editBtnBox}>
                        <button
                          className={`${styles.editBtn}`}
                          onClick={() => openEditForm(ticket)}
                        >
                          <FaEdit />
                          <span className={styles.tooltip}>Edit Status</span>
                        </button>
                      </div>
                    </div>
                  )}
                  <h3>{ticket.subject}</h3>
                  <p>{ticket.description}</p>
                  <span
                    className={`${styles.statusBadge} ${ticket.status.toLowerCase() === "pending"
                      ? styles.pending
                      : ticket.status.toLowerCase() === "in-progress"
                        ? styles.inProgress
                        : styles.resolved
                      }`}
                  >
                    Status : {ticket.status}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </section>

      {/* Message Popup */}
      {viewMessagesPopup && (
        <div className={styles.popupOverlay}>
          <div className={styles.viewMessagePopup}>
            <h2 className={styles.popupTitle}>Messages</h2>
            {messageLoading ? (
              <LoaderSpiner />
            ) : (
              <div className={styles.messageList}>
                {messages.length === 0 ? (
                  <p className={styles.noMessages}>No messages yet.</p>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg._id}
                      className={`${styles.message} ${msg.senderRole === role
                        ? styles.myMessage
                        : styles.theirMessage
                        }`}
                    >
                      <div className={styles.messageHeader}>
                        <div>
                          <strong className={styles.senderRole}>
                            {msg.senderRole.toUpperCase()}
                          </strong>
                          <div>{msg.senderName}</div>
                          <span className={styles.senderId}>
                            &nbsp;•&nbsp;ID: {msg.senderId}
                          </span>
                        </div>

                        <span className={styles.messageTime}>
                          {new Date(msg.createdAt).toLocaleString()}
                        </span>
                      </div>

                      <div className={styles.messageContent}>{msg.message}</div>
                    </div>
                  ))
                )}
              </div>
            )}

            <button
              className={styles.closeBtn}
              onClick={() => setViewMessagesPopup(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {sendMessagePopup && (
        <div className={styles.popupOverlay}>
          <div className={styles.sendMessagePopup}>
            <h2 className={styles.popupTitle}>Send Message</h2>

            <div className={styles.messageForm}>
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className={styles.messageInput}
              />
              <button className={styles.sendBtn} onClick={sendMessage}>
                Send
              </button>
            </div>

            <button
              className={styles.closeBtn}
              onClick={() => setSendMessagePopup(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShowTicket;
//  {messagePopup && (
//   <div className={styles.popupOverlay}>
//     <div className={styles.popup}>
//       <h2 className={styles.popupTitle}>Messages</h2>

//       {messageLoading ? (
//         <div className={styles.loaderWrapper}>
//           <ClipLoader size={30} />
//         </div>
//       ) : (
//         <div className={styles.messageList}>
//           {messages.length === 0 ? (
//             <p className={styles.noMessages}>No messages yet.</p>
//           ) : (
//             messages.map((msg) => (
//               <div
//                 key={msg._id}
//                 className={`${styles.message} ${
//                   msg.senderRole === role ? styles.myMessage : styles.theirMessage
//                 }`}
//               >
//                 <div className={styles.messageMeta}>
//                   <strong>{msg.senderRole}</strong>
//                   <span className={styles.messageTime}>
//                     {new Date(msg.createdAt).toLocaleString()}
//                   </span>
//                 </div>
//                 <div className={styles.messageContent}>{msg.content}</div>
//               </div>
//             ))
//           )}
//         </div>
//       )}

//       {(role === "owner" || role === "subowner") && (
//         <div className={styles.messageForm}>
//           <input
//             type="text"
//             value={newMessage}
//             onChange={(e) => setNewMessage(e.target.value)}
//             placeholder="Type your message..."
//           />
//           <button onClick={sendMessage}>Send</button>
//         </div>
//       )}

//       <button
//         className={styles.closeBtn}
//         onClick={() => setMessagePopup(false)}
//       >
//         Close
//       </button>
//     </div>
//   </div>
// )}

// const openMessages = async (ticket) => {
//   setSelectedTicket(ticket);
//   setMessagePopup(true);
//   setMessageLoading(true);
//   try {
//     const res = await fetch(
//       `https://prod.toxboxai.com/api/tickets/messages/${ticket._id}`,
//       { headers: { Authorization: `Bearer ${token}` } }
//     );
//     const data = await res.json();
//     setMessages(data.messages || []);
//   } catch (err) {
//     toast.error("Error loading messages");
//   }
//   setMessageLoading(false);
// };

// const sendMessage = async () => {
// const token = localStorage.getItem("token");
// const role = localStorage.getItem("role");
// const userId = localStorage.getItem("userId");
//   if (!newMessage.trim()) return;
//   try {
//     const res = await fetch(
//       `https://prod.toxboxai.com/api/tickets/messages/${selectedTicket._id}`,
//       {
//         method: "POST",
//         headers: {
//           Authorization: `Bearer ${token}`,
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify({
//           content: newMessage,
//           senderRole: role,
//           senderId: userId,
//         }),
//       }
//     );
//     const data = await res.json();
//     if (data.success) {
//       setMessages((prev) => [...prev, data.message]);
//       setNewMessage("");
//     }
//   } catch (err) {
//     toast.error("Error sending message");
//   }
// };
